import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import { PLACEHOLDERS, insertPlaceholder } from '../utils/placeholderInsert';

const META_PREFIX = 'HR_BLOCKS_V1:';

const editorModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'blockquote'],
    ['clean'],
  ],
};

const BLOCK_BUTTONS = [
  { type: 'heading', label: 'Heading' },
  { type: 'text', label: 'Text' },
  { type: 'callout', label: 'Callout' },
  { type: 'button', label: 'Button' },
  { type: 'image', label: 'Image' },
  { type: 'variable', label: 'Variable' },
  { type: 'divider', label: 'Divider' },
  { type: 'spacer', label: 'Spacer' },
  { type: 'html', label: 'HTML' },
];

const TEMPLATE_PRESETS = [
  { key: 'announcement', label: 'Announcement' },
  { key: 'reminder', label: 'Reminder' },
  { key: 'newsletter', label: 'Newsletter' },
];

const TEXT_BLOCK_TYPES = new Set(['heading', 'text', 'callout', 'button', 'variable', 'html']);

const createId = () => `blk_${Math.random().toString(36).slice(2, 10)}`;

const encodeText = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const normalizeUrl = (value) => {
  const url = String(value || '').trim();
  if (!url) return '';
  if (/^(https?:\/\/|mailto:|tel:)/i.test(url)) return url;
  return `https://${url}`;
};

const toBase64 = (value) => {
  const text = String(value || '');

  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    return window.btoa(unescape(encodeURIComponent(text)));
  }

  return '';
};

const fromBase64 = (value) => {
  const text = String(value || '');

  if (typeof window !== 'undefined' && typeof window.atob === 'function') {
    return decodeURIComponent(escape(window.atob(text)));
  }

  return '';
};

const stripBuilderMeta = (html = '') => String(html || '').replace(/^\s*<!--\s*HR_BLOCKS_V1:[A-Za-z0-9+/=]+\s*-->\s*/i, '');

const firstPlaceholder = (placeholders = []) => String((placeholders || [])[0] || '{{first_name}}');

const createBlock = (type, placeholders = [], forcedToken = '') => {
  const token = forcedToken || firstPlaceholder(placeholders);

  if (type === 'heading') {
    return {
      id: createId(),
      type: 'heading',
      text: 'Email title',
    };
  }

  if (type === 'text') {
    return {
      id: createId(),
      type: 'text',
      text: 'Write your message here.',
    };
  }

  if (type === 'callout') {
    return {
      id: createId(),
      type: 'callout',
      text: 'Important announcement goes here.',
    };
  }

  if (type === 'button') {
    return {
      id: createId(),
      type: 'button',
      text: 'Open Link',
      url: 'https://',
    };
  }

  if (type === 'image') {
    return {
      id: createId(),
      type: 'image',
      url: '',
      alt: '',
    };
  }

  if (type === 'variable') {
    return {
      id: createId(),
      type: 'variable',
      token,
    };
  }

  if (type === 'divider') {
    return {
      id: createId(),
      type: 'divider',
    };
  }

  if (type === 'spacer') {
    return {
      id: createId(),
      type: 'spacer',
      height: 24,
    };
  }

  return {
    id: createId(),
    type: 'html',
    html: '<p>Custom HTML block</p>',
  };
};

const normalizeBlocks = (blocks, placeholders = []) => {
  const token = firstPlaceholder(placeholders);
  const safeBlocks = (Array.isArray(blocks) ? blocks : [])
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const type = String(item.type || 'text');
      if (type === 'heading') {
        return { id: String(item.id || createId()), type, text: String(item.text || '') };
      }
      if (type === 'text') {
        return { id: String(item.id || createId()), type, text: String(item.text || '') };
      }
      if (type === 'callout') {
        return { id: String(item.id || createId()), type, text: String(item.text || '') };
      }
      if (type === 'button') {
        return {
          id: String(item.id || createId()),
          type,
          text: String(item.text || ''),
          url: String(item.url || ''),
        };
      }
      if (type === 'image') {
        return {
          id: String(item.id || createId()),
          type,
          url: String(item.url || ''),
          alt: String(item.alt || ''),
        };
      }
      if (type === 'variable') {
        return { id: String(item.id || createId()), type, token: String(item.token || token) || token };
      }
      if (type === 'divider') {
        return { id: String(item.id || createId()), type };
      }
      if (type === 'spacer') {
        const height = Number(item.height);
        return {
          id: String(item.id || createId()),
          type,
          height: Number.isFinite(height) ? Math.max(4, Math.min(160, height)) : 24,
        };
      }

      return { id: String(item.id || createId()), type: 'html', html: String(item.html || '') };
    });

  return safeBlocks.length ? safeBlocks : [createBlock('text', placeholders)];
};

const parseBlocks = (html, placeholders = []) => {
  const source = String(html || '');
  const marker = source.match(/^\s*<!--\s*HR_BLOCKS_V1:([A-Za-z0-9+/=]+)\s*-->/i);

  if (marker?.[1]) {
    try {
      const parsed = JSON.parse(fromBase64(marker[1]));
      return normalizeBlocks(parsed, placeholders);
    } catch {
      // fallback below
    }
  }

  const plain = stripBuilderMeta(source).trim();
  if (plain) {
    return normalizeBlocks([{ type: 'html', html: plain }], placeholders);
  }

  return normalizeBlocks([createBlock('text', placeholders)], placeholders);
};

const renderBlockHtml = (block) => {
  if (block.type === 'heading') {
    return `<h2 style="margin:0 0 12px 0;font-size:22px;line-height:1.3;color:#0f172a;">${encodeText(block.text || '').replace(/\r?\n/g, '<br />')}</h2>`;
  }

  if (block.type === 'text') {
    return `<p>${encodeText(block.text || '').replace(/\r?\n/g, '<br />')}</p>`;
  }

  if (block.type === 'callout') {
    return `<div style="background:#f1f5f9;border-left:4px solid #0ea5e9;padding:12px 14px;margin:12px 0;border-radius:6px;"><p style="margin:0;">${encodeText(block.text || '').replace(/\r?\n/g, '<br />')}</p></div>`;
  }

  if (block.type === 'button') {
    const href = normalizeUrl(block.url);
    if (!href) {
      return `<p>${encodeText(block.text || '')}</p>`;
    }

    return `<p><a href="${encodeText(href)}" target="_blank" rel="noopener noreferrer">${encodeText(block.text || 'Open Link')}</a></p>`;
  }

  if (block.type === 'image') {
    const src = normalizeUrl(block.url);
    if (!src) return '';
    return `<p><img src="${encodeText(src)}" alt="${encodeText(block.alt || '')}" width="560" /></p>`;
  }

  if (block.type === 'variable') {
    return `<p>${encodeText(block.token || '{{first_name}}')}</p>`;
  }

  if (block.type === 'divider') {
    return '<hr />';
  }

  if (block.type === 'spacer') {
    return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0"><tr><td height="${Number(block.height || 24)}">&nbsp;</td></tr></table>`;
  }

  return String(block.html || '');
};

const buildHtml = (blocks, placeholders = []) => {
  const normalized = normalizeBlocks(blocks, placeholders);
  const encoded = toBase64(JSON.stringify(normalized));
  const body = normalized
    .map((item) => renderBlockHtml(item))
    .filter((item) => String(item || '').trim() !== '')
    .join('\n');

  return `<!-- ${META_PREFIX}${encoded} -->\n${body}`;
};

const addTokenToBlock = (block, token) => {
  const nextToken = String(token || '').trim();
  if (!nextToken) return block;

  if (block.type === 'heading') {
    return { ...block, text: `${String(block.text || '')}${nextToken}` };
  }

  if (block.type === 'text') {
    return { ...block, text: `${String(block.text || '')}${nextToken}` };
  }

  if (block.type === 'callout') {
    return { ...block, text: `${String(block.text || '')}${nextToken}` };
  }

  if (block.type === 'button') {
    return { ...block, text: `${String(block.text || '')}${nextToken}` };
  }

  if (block.type === 'variable') {
    return { ...block, token: nextToken };
  }

  if (block.type === 'html') {
    return { ...block, html: `${String(block.html || '')}${nextToken}` };
  }

  return block;
};

const buildTemplateBlocks = (templateKey, placeholders = []) => {
  const firstToken = firstPlaceholder(placeholders);

  if (templateKey === 'announcement') {
    return normalizeBlocks([
      { type: 'heading', text: 'Company Announcement' },
      { type: 'text', text: `Hello ${firstToken},\n\nWe have an important update to share with you.` },
      { type: 'callout', text: 'Please review the update carefully and contact HR if you need clarification.' },
      { type: 'button', text: 'Read Full Details', url: 'https://' },
      { type: 'text', text: 'Thank you,\nHR Team' },
    ], placeholders);
  }

  if (templateKey === 'reminder') {
    return normalizeBlocks([
      { type: 'heading', text: 'Friendly Reminder' },
      { type: 'text', text: `Hi ${firstToken},\n\nThis is a reminder regarding your pending task.` },
      { type: 'divider' },
      { type: 'callout', text: 'Please complete this before the deadline to avoid delays.' },
      { type: 'button', text: 'Open Task', url: 'https://' },
    ], placeholders);
  }

  if (templateKey === 'newsletter') {
    return normalizeBlocks([
      { type: 'heading', text: 'Monthly HR Newsletter' },
      { type: 'image', url: 'https://', alt: 'Newsletter banner' },
      { type: 'text', text: `Hello ${firstToken},\n\nHere are this month's highlights and updates.` },
      { type: 'button', text: 'Read More', url: 'https://' },
      { type: 'text', text: 'Regards,\nHR Team' },
    ], placeholders);
  }

  return normalizeBlocks([createBlock('text', placeholders)], placeholders);
};

const prettyToken = (token) => {
  const clean = String(token || '').replace(/[{}]/g, '').replace(/_/g, ' ').trim();
  return clean || token;
};

export default function EmailCampaignContentStep({
  value,
  onChange,
  placeholders = [],
  onSendTest,
  sendTestLoading = false,
  onPreview,
  readOnly = false,
}) {
  const [languageTab, setLanguageTab] = useState('en');
  const [editorMode, setEditorMode] = useState('builder');
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [templateSelection, setTemplateSelection] = useState('');

  const currentHtmlField = useMemo(() => {
    if (value?.is_bilingual) {
      return languageTab === 'ar' ? 'html_body_ar' : 'html_body_en';
    }

    return 'html_body';
  }, [languageTab, value?.is_bilingual]);

  const currentHtmlValue = String(value?.[currentHtmlField] || '');
  const sourceHtmlValue = useMemo(() => stripBuilderMeta(currentHtmlValue), [currentHtmlValue]);

  const availablePlaceholders = useMemo(() => {
    const list = (Array.isArray(placeholders) && placeholders.length ? placeholders : PLACEHOLDERS)
      .map((item) => String(item || '').trim())
      .filter(Boolean);

    return list.length ? list : ['{{first_name}}'];
  }, [placeholders]);

  const [builderBlocks, setBuilderBlocks] = useState(() => parseBlocks(currentHtmlValue, availablePlaceholders));
  const lastBuilderHtmlByFieldRef = useRef({});

  const updateField = (field, fieldValue) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const updateCurrentHtml = (html) => {
    updateField(currentHtmlField, html);
  };

  const commitBlocks = (nextBlocks) => {
    const normalized = normalizeBlocks(nextBlocks, availablePlaceholders);
    setBuilderBlocks(normalized);
    setSelectedBlockId((prev) => (
      normalized.some((item) => item.id === prev) ? prev : normalized[0]?.id || null
    ));

    const html = buildHtml(normalized, availablePlaceholders);
    lastBuilderHtmlByFieldRef.current[currentHtmlField] = html;
    updateCurrentHtml(html);
  };

  useEffect(() => {
    const latest = String(currentHtmlValue || '');
    const internal = String(lastBuilderHtmlByFieldRef.current[currentHtmlField] || '');

    if (latest === internal) return;

    const parsed = parseBlocks(latest, availablePlaceholders);
    setBuilderBlocks(parsed);
    setSelectedBlockId(parsed[0]?.id || null);
  }, [availablePlaceholders, currentHtmlField, currentHtmlValue]);

  const { quill, quillRef } = useQuill({
    modules: editorModules,
    theme: 'snow',
    readOnly,
  });

  useEffect(() => {
    if (!quill || editorMode !== 'rich') return;

    const syncFromEditor = () => {
      updateCurrentHtml(stripBuilderMeta(quill.root.innerHTML));
    };

    quill.on('text-change', syncFromEditor);

    return () => {
      quill.off('text-change', syncFromEditor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quill, currentHtmlField, editorMode]);

  useEffect(() => {
    if (!quill) return;

    if (editorMode !== 'rich') {
      quill.enable(false);
      return;
    }

    if (quill.root.innerHTML !== sourceHtmlValue) {
      quill.root.innerHTML = sourceHtmlValue;
    }

    const isArabic = Boolean(value?.is_bilingual && languageTab === 'ar');
    quill.root.setAttribute('dir', isArabic ? 'rtl' : 'ltr');
    quill.root.style.textAlign = isArabic ? 'right' : 'left';
    quill.enable(!readOnly);
  }, [quill, editorMode, sourceHtmlValue, languageTab, readOnly, value?.is_bilingual]);

  const addBlock = (type) => {
    const block = createBlock(type, availablePlaceholders);
    const next = [...builderBlocks, block];
    commitBlocks(next);
    setSelectedBlockId(block.id);
  };

  const applyTemplate = (templateKey) => {
    const blocks = buildTemplateBlocks(templateKey, availablePlaceholders);
    commitBlocks(blocks);
    setSelectedBlockId(blocks[0]?.id || null);
  };

  const removeBlock = (blockId) => {
    const next = builderBlocks.filter((item) => item.id !== blockId);
    commitBlocks(next);
  };

  const patchBlock = (blockId, patch) => {
    const next = builderBlocks.map((item) => (item.id === blockId ? { ...item, ...patch } : item));
    commitBlocks(next);
  };

  const moveBlock = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= builderBlocks.length || toIndex >= builderBlocks.length) return;

    const next = [...builderBlocks];
    const [picked] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, picked);
    commitBlocks(next);
  };

  const insertTokenInBlock = (blockId, token) => {
    const next = builderBlocks.map((item) => (item.id === blockId ? addTokenToBlock(item, token) : item));
    commitBlocks(next);
    setSelectedBlockId(blockId);
  };

  const handleInsertPlaceholder = (tokenValue) => {
    if (readOnly) return;

    const token = String(tokenValue || '').trim();
    if (!token) return;

    if (editorMode === 'builder') {
      const selectedIndex = builderBlocks.findIndex((item) => item.id === selectedBlockId);
      const fallbackIndex = builderBlocks.findIndex((item) => TEXT_BLOCK_TYPES.has(item.type));
      const targetIndex = selectedIndex >= 0 ? selectedIndex : fallbackIndex;

      if (targetIndex >= 0) {
        const blockId = builderBlocks[targetIndex].id;
        insertTokenInBlock(blockId, token);
      } else {
        const block = createBlock('variable', availablePlaceholders, token);
        commitBlocks([...builderBlocks, block]);
        setSelectedBlockId(block.id);
      }
      return;
    }

    if (editorMode === 'rich' && quill) {
      const selection = quill.getSelection(true);
      const index = selection?.index ?? quill.getLength();
      quill.insertText(index, token);
      quill.setSelection(index + token.length, 0);
      updateCurrentHtml(stripBuilderMeta(quill.root.innerHTML));
      return;
    }

    updateCurrentHtml(insertPlaceholder(sourceHtmlValue, token));
  };

  const sendTest = () => {
    if (!onSendTest) return;
    onSendTest({ email: testEmail, name: testName });
  };

  const renderBlockFields = (block) => {
    if (block.type === 'heading') {
      return (
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Heading"
            value={block.text || ''}
            onChange={(event) => patchBlock(block.id, { text: event.target.value })}
            disabled={readOnly}
          />
          <TextField
            select
            size="small"
            label="Insert variable"
            value=""
            onChange={(event) => insertTokenInBlock(block.id, event.target.value)}
            disabled={readOnly}
            sx={{ maxWidth: 280 }}
          >
            {availablePlaceholders.map((token) => (
              <MenuItem key={`${block.id}-heading-${token}`} value={token}>
                {token}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      );
    }

    if (block.type === 'text') {
      return (
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Text"
            value={block.text || ''}
            onChange={(event) => patchBlock(block.id, { text: event.target.value })}
            disabled={readOnly}
          />
          <TextField
            select
            size="small"
            label="Insert variable"
            value=""
            onChange={(event) => insertTokenInBlock(block.id, event.target.value)}
            disabled={readOnly}
            sx={{ maxWidth: 280 }}
          >
            {availablePlaceholders.map((token) => (
              <MenuItem key={`${block.id}-${token}`} value={token}>
                {token}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      );
    }

    if (block.type === 'callout') {
      return (
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Callout text"
            value={block.text || ''}
            onChange={(event) => patchBlock(block.id, { text: event.target.value })}
            disabled={readOnly}
          />
          <TextField
            select
            size="small"
            label="Insert variable"
            value=""
            onChange={(event) => insertTokenInBlock(block.id, event.target.value)}
            disabled={readOnly}
            sx={{ maxWidth: 280 }}
          >
            {availablePlaceholders.map((token) => (
              <MenuItem key={`${block.id}-callout-${token}`} value={token}>
                {token}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      );
    }

    if (block.type === 'button') {
      return (
        <Grid container spacing={1.25} sx={{ mt: 1 }}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Button text"
              value={block.text || ''}
              onChange={(event) => patchBlock(block.id, { text: event.target.value })}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="URL"
              placeholder="https://example.com"
              value={block.url || ''}
              onChange={(event) => patchBlock(block.id, { url: event.target.value })}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Variable"
              value=""
              onChange={(event) => insertTokenInBlock(block.id, event.target.value)}
              disabled={readOnly}
            >
              {availablePlaceholders.map((token) => (
                <MenuItem key={`${block.id}-btn-${token}`} value={token}>
                  {token}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      );
    }

    if (block.type === 'image') {
      return (
        <Grid container spacing={1.25} sx={{ mt: 1 }}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Image URL"
              placeholder="https://..."
              value={block.url || ''}
              onChange={(event) => patchBlock(block.id, { url: event.target.value })}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Alt text"
              value={block.alt || ''}
              onChange={(event) => patchBlock(block.id, { alt: event.target.value })}
              disabled={readOnly}
            />
          </Grid>
        </Grid>
      );
    }
    if (block.type === 'variable') {
      return (
        <Grid container spacing={1.25} sx={{ mt: 1 }}>
          <Grid item xs={12} md={5}>
            <TextField
              select
              fullWidth
              label="Variable token"
              value={block.token || firstPlaceholder(availablePlaceholders)}
              onChange={(event) => patchBlock(block.id, { token: event.target.value })}
              disabled={readOnly}
            >
              {availablePlaceholders.map((token) => (
                <MenuItem key={`${block.id}-var-${token}`} value={token}>
                  {token}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      );
    }

    if (block.type === 'spacer') {
      return (
        <Grid container spacing={1.25} sx={{ mt: 1 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Height (px)"
              value={Number(block.height || 24)}
              onChange={(event) => patchBlock(block.id, { height: Number(event.target.value) })}
              disabled={readOnly}
            />
          </Grid>
        </Grid>
      );
    }

    if (block.type === 'divider') {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Divider block renders a horizontal line.
        </Typography>
      );
    }

    return (
      <Stack spacing={1.25} sx={{ mt: 1 }}>
        <TextField
          fullWidth
          multiline
          minRows={8}
          label="Custom HTML"
          value={block.html || ''}
          onChange={(event) => patchBlock(block.id, { html: event.target.value })}
          inputProps={{ dir: value?.is_bilingual && languageTab === 'ar' ? 'rtl' : 'ltr' }}
          disabled={readOnly}
        />
        <TextField
          select
          size="small"
          label="Insert variable"
          value=""
          onChange={(event) => insertTokenInBlock(block.id, event.target.value)}
          disabled={readOnly}
          sx={{ maxWidth: 280 }}
        >
          {availablePlaceholders.map((token) => (
            <MenuItem key={`${block.id}-html-${token}`} value={token}>
              {token}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    );
  };

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Subject"
            fullWidth
            required
            value={value?.subject || ''}
            onChange={(event) => updateField('subject', event.target.value)}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Preheader"
            fullWidth
            value={value?.preheader || ''}
            onChange={(event) => updateField('preheader', event.target.value)}
            disabled={readOnly}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="From Name"
            fullWidth
            value={value?.from_name || ''}
            onChange={(event) => updateField('from_name', event.target.value)}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="From Email"
            fullWidth
            value={value?.from_email || ''}
            onChange={(event) => updateField('from_email', event.target.value)}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Reply-To"
            fullWidth
            value={value?.reply_to || ''}
            onChange={(event) => updateField('reply_to', event.target.value)}
            disabled={readOnly}
          />
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
        <FormControlLabel
          control={(
            <Switch
              checked={Boolean(value?.is_bilingual)}
              onChange={(event) => updateField('is_bilingual', event.target.checked)}
              disabled={readOnly}
            />
          )}
          label="Bilingual content (EN + AR)"
        />

        <TextField
          select
          size="small"
          label="Editor mode"
          value={editorMode}
          sx={{ minWidth: 220 }}
          onChange={(event) => setEditorMode(event.target.value)}
          disabled={readOnly}
        >
          <MenuItem value="builder">Visual Builder (Drag & Drop)</MenuItem>
          <MenuItem value="rich">Rich Text</MenuItem>
          <MenuItem value="html">HTML Source</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Insert variable"
          value=""
          sx={{ minWidth: 210 }}
          onChange={(event) => handleInsertPlaceholder(event.target.value)}
          disabled={readOnly}
        >
          {availablePlaceholders.map((token) => (
            <MenuItem key={token} value={token}>
              {token}
            </MenuItem>
          ))}
        </TextField>

        <Button variant="outlined" onClick={onPreview}>
          Preview
        </Button>
      </Stack>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {availablePlaceholders.map((token) => (
          <Chip
            key={token}
            label={prettyToken(token)}
            size="small"
            variant="outlined"
            clickable={!readOnly}
            onClick={readOnly ? undefined : () => handleInsertPlaceholder(token)}
          />
        ))}
      </Box>

      {value?.is_bilingual ? (
        <Tabs value={languageTab} onChange={(_, next) => setLanguageTab(next)}>
          <Tab label="English" value="en" />
          <Tab label="Arabic" value="ar" />
        </Tabs>
      ) : null}

      {editorMode === 'builder' ? (
        <Stack spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'grey.50' }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Add block</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {BLOCK_BUTTONS.map((option) => (
                  <Button
                    key={option.type}
                    size="small"
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => addBlock(option.type)}
                    disabled={readOnly}
                  >
                    {option.label}
                  </Button>
                ))}
              </Box>
              <TextField
                select
                size="small"
                label="Quick template"
                value={templateSelection}
                onChange={(event) => {
                  const selected = event.target.value;
                  if (selected) {
                    applyTemplate(selected);
                    setTemplateSelection('');
                  } else {
                    setTemplateSelection('');
                  }
                }}
                disabled={readOnly}
                sx={{ maxWidth: 280 }}
              >
                <MenuItem value="">Choose template</MenuItem>
                {TEMPLATE_PRESETS.map((template) => (
                  <MenuItem key={template.key} value={template.key}>
                    {template.label}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="caption" color="text.secondary">
                Drag cards to reorder layout. Variable insertion targets the selected block.
              </Typography>
            </Stack>
          </Paper>

          {builderBlocks.map((block, index) => {
            const isSelected = selectedBlockId === block.id;
            const isDropTarget = dragOverIndex === index;

            return (
              <Paper
                key={block.id}
                variant="outlined"
                draggable={!readOnly}
                onClick={() => setSelectedBlockId(block.id)}
                onDragStart={() => {
                  if (readOnly) return;
                  setDragIndex(index);
                  setSelectedBlockId(block.id);
                }}
                onDragOver={(event) => {
                  if (readOnly) return;
                  event.preventDefault();
                  setDragOverIndex(index);
                }}
                onDrop={(event) => {
                  if (readOnly) return;
                  event.preventDefault();
                  if (dragIndex !== null) {
                    moveBlock(dragIndex, index);
                  }
                  setDragIndex(null);
                  setDragOverIndex(null);
                }}
                onDragEnd={() => {
                  setDragIndex(null);
                  setDragOverIndex(null);
                }}
                sx={{
                  p: 1.5,
                  cursor: readOnly ? 'default' : 'grab',
                  borderWidth: isSelected ? 2 : 1,
                  borderColor: isSelected ? 'primary.main' : isDropTarget ? 'secondary.main' : 'divider',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DragIndicatorIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2">
                      {index + 1}. {BLOCK_BUTTONS.find((item) => item.type === block.type)?.label || 'Block'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.25}>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => moveBlock(index, Math.max(0, index - 1))}
                          disabled={readOnly || index === 0}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => moveBlock(index, Math.min(builderBlocks.length - 1, index + 1))}
                          disabled={readOnly || index === builderBlocks.length - 1}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete block">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeBlock(block.id)}
                          disabled={readOnly}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
                {renderBlockFields(block)}
              </Paper>
            );
          })}
        </Stack>
      ) : editorMode === 'html' ? (
        <TextField
          multiline
          minRows={14}
          fullWidth
          value={sourceHtmlValue}
          onChange={(event) => updateCurrentHtml(event.target.value)}
          inputProps={{ dir: value?.is_bilingual && languageTab === 'ar' ? 'rtl' : 'ltr' }}
          disabled={readOnly}
        />
      ) : (
        <Box
          sx={{
            '& .ql-editor': {
              minHeight: 320,
            },
          }}
        >
          <div ref={quillRef} />
        </Box>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            label="Test email"
            placeholder="qa@example.com"
            value={testEmail}
            onChange={(event) => setTestEmail(event.target.value)}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Recipient name (optional)"
            value={testName}
            onChange={(event) => setTestName(event.target.value)}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            fullWidth
            variant="contained"
            onClick={sendTest}
            disabled={readOnly || sendTestLoading || !String(testEmail || '').trim()}
            sx={{ height: '100%' }}
          >
            {sendTestLoading ? 'Sending...' : 'Send Test'}
          </Button>
        </Grid>
      </Grid>

      <Alert severity="info">
        Use placeholders like <strong>{'{{first_name}}'}</strong> and <strong>{'{{company_name}}'}</strong> for recipient personalization.
        Visual Builder saves layout blocks and still stores standard HTML for sending.
      </Alert>

      <Typography variant="caption" color="text.secondary">
        Arabic tab renders with RTL (`dir="rtl"`) and right-aligned content in preview/send.
      </Typography>
    </Stack>
  );
}
